/** Authoring expectations, not self-certified animation evidence. */
import { z } from 'zod';
import { LessonCard } from './lessons';

const EventId = z.string().min(1).max(80).regex(/^[a-zA-Z0-9_-]+$/);
const base = { id: EventId, event_refs: z.array(EventId).min(1).max(256) };
const CountClaim = z.strictObject({
  ...base, kind: z.literal('count'), entity_ids: z.array(EventId).max(100).default([]),
  cycle_event_ids: z.array(EventId).max(100).default([]), expected: z.int().min(0).max(100),
}).refine((c) => Boolean(c.entity_ids.length) !== Boolean(c.cycle_event_ids.length)
  && new Set(c.entity_ids).size === c.entity_ids.length && new Set(c.cycle_event_ids).size === c.cycle_event_ids.length,
{ message: 'Count one distinct collection of entities or cycle events' });
const MovementClaim = z.strictObject({
  ...base, kind: z.literal('movement'), object: EventId,
  direction: z.enum(['left', 'right', 'up', 'down', 'toward', 'away']), target: EventId.nullable().optional(),
}).refine((c) => !['toward', 'away'].includes(c.direction) || Boolean(c.target), { message: 'Relative movement requires a target' });
export const EventClaim = z.discriminatedUnion('kind', [
  CountClaim,
  z.strictObject({ ...base, kind: z.literal('gesture'), actor: EventId, action: z.string().min(1).max(80), expected_cycles: z.int().min(1).max(100) }),
  z.strictObject({ ...base, kind: z.literal('transfer'), object: EventId, from_holder: EventId.nullable(), to_holder: EventId.nullable() }),
  MovementClaim,
  z.strictObject({ ...base, kind: z.literal('appearance'), object: EventId, visible: z.boolean() }),
  z.strictObject({ ...base, kind: z.literal('lesson_fact'), fact_id: z.string().min(1).max(100) }),
  z.strictObject({ ...base, kind: z.literal('daily_record'), start: z.int().min(0), end: z.int().min(1), quote: z.string().min(1).max(1200) })
    .refine((c) => c.end > c.start, { message: 'Daily source span must be ordered' }),
]);
export type EventClaim = z.infer<typeof EventClaim>;

/** Wire form of existing registry clauses; the registry remains their evaluator. */
const ref = z.string().min(1);
const roleSpec = {
  types: z.array(z.enum(['character', 'object', 'scenery', 'feature', 'spot'])).optional(),
  kinds_any: z.array(ref).optional(), plans_any: z.array(ref).optional(),
  capabilities_all: z.array(ref).optional(), capabilities_any: z.array(ref).optional(),
  needs: z.array(ref).optional(), state: z.record(z.string(), z.json()).optional(), required: z.boolean().optional(),
};
// The recursive shape is bounded by the backend's authoring limits and registry validation.
export const EventPredicate: z.ZodType = z.lazy(() => z.union([
  z.strictObject({ k: z.literal('role'), role: ref, ...roleSpec }),
  z.strictObject({ k: z.literal('relation'), a: ref, rel: ref, b: ref, ratio: z.number().positive().max(100).nullable().optional() }),
  z.strictObject({ k: z.literal('stage'), has: z.array(ref).optional(), world_any: z.array(ref).optional() }),
  z.strictObject({ k: z.literal('state'), ref, eq: z.json() }),
  z.strictObject({ k: z.literal('state'), ref, ne: z.json() }),
  z.strictObject({ k: z.literal('state'), ref, in: z.array(z.json()) }),
  z.strictObject({ k: z.literal('any'), any: z.array(EventPredicate).min(1) }),
  z.strictObject({ k: z.literal('not'), not: EventPredicate }),
]));

export const LINE_EMOTIONS = [
  'happy', 'excited', 'proud', 'silly', 'surprised', 'amazed', 'sad', 'sorry', 'disappointed',
  'angry', 'cross', 'grumpy', 'frustrated', 'scared', 'nervous', 'worried', 'shy', 'curious', 'thinking', 'wondering', 'puzzled',
] as const;

export const SpeechSegment = z.strictObject({
  id: EventId, speaker: z.string().min(1).max(80), text: z.string().min(1).max(800),
  relation: z.enum(['before', 'during', 'after', 'recollection']),
  anchor_word_index: z.int().min(0).nullable().optional(),
  /** How the line is said — the player shows it in the body (a happy hop, a sad droop…). */
  emotion: z.enum(LINE_EMOTIONS).nullable().optional(),
});
export type SpeechSegment = z.infer<typeof SpeechSegment>;
export const StoryEvent = z.strictObject({
  id: EventId, chapter: EventId,
  kind: z.enum(['action', 'observation', 'prediction', 'explanation', 'reflection', 'resolution']),
  dependencies: z.array(EventId).max(256).default([]), purpose: z.string().min(1).max(500),
  registry_action: z.string().min(1).max(80).nullable().optional(), bindings: z.record(z.string(), z.string()).default({}),
  preconditions: z.array(EventPredicate).default([]), results: z.array(EventPredicate).default([]),
  claims: z.array(EventClaim).max(30).default([]), source_refs: z.array(z.string()).max(30).default([]),
  focus: EventId.nullable().optional(), speech: z.array(SpeechSegment).max(20).default([]),
  think_s: z.number().min(0).max(8).default(0), effects: z.array(z.enum(['focus', 'count', 'contact', 'gentle'])).max(4).default([]),
});
export type StoryEvent = z.infer<typeof StoryEvent>;
export const EventPlan = z.strictObject({
  format: z.literal('events@1'), objective_snapshot: LessonCard.nullable(),
  topic: z.enum(['story', 'learn', 'grow', 'bedtime', 'today']).default('story'),
  source_prompt: z.string().max(1200).default(''), title: z.string().max(160).default(''),
  chapters: z.array(z.strictObject({ id: EventId, purpose: z.string().min(1).max(500), resolution_event: EventId })).min(1).max(8),
  events: z.array(StoryEvent).min(1).max(256),
}).meta({ id: 'EventPlan', title: 'Causal story event plan' });
export type EventPlan = z.infer<typeof EventPlan>;
export const EventIssue = z.strictObject({ code: z.string(), event_id: z.string().nullable().optional(), message: z.string(), blocking: z.boolean().default(true) });
export type EventIssue = z.infer<typeof EventIssue>;
